import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Spinner,
  Stack,
  TextInput,
  toast,
} from 'component-library';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { PostOrganizationPlotGroupsRequestBodyData } from '@/api/rest/resources/types/organization';
import { PlotGroup } from '@/api/rest/resources/types/plotGroup';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SuspenseBoundary,
  useFormField,
} from '@/components';
import { usePlotGroupDefault } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { Logger } from '@/lib/logs/logger';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

import {
  CreateEditGroupSubmitProps,
  useCreateGroup,
  useEditGroup,
  usePlotGroupInvalidateQueries,
} from '../../hooks/usePlotGroup';

export type EditGroupProps = {
  organizationId: string;
  plotGroup: PlotGroup | null;
  open: boolean;
  onOpenChange: DialogProps['onOpenChange'];
  additionalPlots?: string[];
};

export const EditGroup = ({ organizationId, open, onOpenChange, plotGroup, additionalPlots }: EditGroupProps) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useEditGroup();
  assertIsDefined(organizationId, 'organizationId');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid='edit-plot-group'>
        <DialogHeader>
          <DialogTitle className='truncate'>
            {t('global.organization.plotGroup.editPlotGroup.title', { name: plotGroup?.name })}
          </DialogTitle>
        </DialogHeader>
        <SuspenseBoundary
          fallback={
            <Stack centerMain>
              <Spinner size={10} />
            </Stack>
          }
        >
          <Form
            onSuccess={() => onOpenChange?.(false)}
            plotGroup={plotGroup}
            additionalPlots={additionalPlots}
            organizationId={organizationId}
            submit={submit}
            isSubmitting={isSubmitting}
          />
        </SuspenseBoundary>
      </DialogContent>
    </Dialog>
  );
};

export const CreateGroup = ({ organizationId, open, onOpenChange, plotGroup, additionalPlots }: EditGroupProps) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useCreateGroup();
  assertIsDefined(organizationId, 'organizationId');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid='edit-plot-group'>
        <DialogHeader>
          <DialogTitle>
            {!isNullish(additionalPlots) && additionalPlots?.length > 0
              ? t('global.organization.plotGroup.createPlotGroup.title', { count: additionalPlots?.length })
              : t('global.organization.plotGroup.createPlotGroup.default_title')}
          </DialogTitle>
        </DialogHeader>
        <SuspenseBoundary
          fallback={
            <Stack centerMain>
              <Spinner size={10} />
            </Stack>
          }
        >
          <Form
            onSuccess={() => onOpenChange?.(false)}
            plotGroup={plotGroup}
            additionalPlots={additionalPlots}
            organizationId={organizationId}
            submit={submit}
            isSubmitting={isSubmitting}
          />
        </SuspenseBoundary>
      </DialogContent>
    </Dialog>
  );
};

export type EditGroupFields = Partial<PostOrganizationPlotGroupsRequestBodyData>;

const Form = ({
  submit,
  isSubmitting,
  onSuccess,
  plotGroup,
  organizationId,
  additionalPlots,
}: {
  submit: (props: CreateEditGroupSubmitProps) => void;
  isSubmitting: boolean;
  onSuccess: () => void;
  plotGroup: PlotGroup | null;
  organizationId: string;
  additionalPlots?: string[];
}) => {
  const { t } = useTranslation();

  const form = useForm<EditGroupFields>({
    defaultValues: {
      name: '',
    },
  });

  const isFormPristine = !form.formState.isDirty;
  const isRequiredLabel = t('global.ui.form.input.required');

  const defaultPlotGroup = usePlotGroupDefault('default');
  const { invalidatePlotGroupRelatedQueries } = usePlotGroupInvalidateQueries();

  const onSubmit: SubmitHandler<EditGroupFields> = async (inputs) => {
    try {
      form.clearErrors();
      const prevPlotGroup = plotGroup ?? defaultPlotGroup;
      submit({
        organizationId,
        plotGroup: {
          ...prevPlotGroup,
          name: inputs.name ?? null,
        },
        additionalPlots: additionalPlots ?? [],
        onSuccess: () => invalidatePlotGroupRelatedQueries(organizationId),
      } as CreateEditGroupSubmitProps);
      onSuccess();
      form.reset();
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }

      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof EditGroupFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };

  return (
    <FormProvider {...form}>
      <DialogBody>
        <form id='edit-plot-group-form' onSubmit={form.handleSubmit(onSubmit)} data-testid='edit-plot-group-form'>
          <fieldset disabled={isSubmitting}>
            <Stack spacing={8}>
              <FormField
                control={form.control}
                name='name'
                rules={{
                  required: isRequiredLabel,
                }}
                render={({ field: { value, ...field } }) => (
                  <FormItem className='flex flex-col gap-1'>
                    <CustomFormLabel>
                      {t('global.organization.plotGroup.createPlotGroup.form.name.label')}*
                    </CustomFormLabel>
                    <FormControl>
                      <TextInput
                        value={value}
                        placeholder={t('global.organization.plotGroup.createPlotGroup.form.name.placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <CustomFormMessage>{form.formState.errors.name?.message}</CustomFormMessage>
                    <FormDescription>
                      {t('global.organization.plotGroup.createPlotGroup.form.name.helper')}
                    </FormDescription>
                  </FormItem>
                )}
              />
            </Stack>
          </fieldset>
        </form>
      </DialogBody>
      <DialogFooter className='mt-12'>
        <DialogClose asChild>
          <Button variant='text' size='small' disabled={isSubmitting}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogClose>
        <Button
          form='edit-plot-group-form'
          size='small'
          type='submit'
          disabled={isSubmitting || isFormPristine}
          loading={isSubmitting}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </DialogFooter>
    </FormProvider>
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};

const CustomFormMessage: FC<PropsWithChildren> = ({ children }) => (
  <FormMessage className='typography-inputLabel mt-0 text-error'>{children}</FormMessage>
);
